import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { LockOutlined, RemoveRedEyeOutlined, CheckCircle } from '@mui/icons-material';

import { StepComponentProps } from 'components/Flow/RenderMachine';
import { gray, gray4, green, lighterGray, mediumGray } from 'utils/color-styles';
import { NonprofitOnboardingFlowContext } from 'components/Flow/flows/nonprofitOnboarding/context';

export interface StepData {
  title: string | React.JSX.Element;
  subTitle: string | React.JSX.Element;
  onboardingTrackerTitle?: string | React.JSX.Element;
  // return true to skip the modal from opening
  action?: (checked: boolean) => boolean;
  checked: boolean;
  loading?: boolean;
  enabled: boolean;
  ModalComponent: (
    props: StepComponentProps & { standloneMode?: boolean }
  ) => React.JSX.Element | null;
}

export const Step = ({
  step,
  index,
  isLast,
}: {
  step: StepData;
  index: number;
  isLast: boolean;
}) => {
  const { ModalComponent } = step;
  const [id, setId] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <Box display="grid" gridTemplateColumns="auto 1fr auto" gap={1} overflow="hidden" mb={0.5}>
        <Box whiteSpace="nowrap" flexGrow={0} mr={1} mt={0.5}>
          {step.enabled ? (
            step.loading ? (
              <CircularProgress size={26} />
            ) : step.checked ? (
              <CheckCircle style={{ color: green, width: 30, height: 30, margin: -2 }} />
            ) : (
              <div
                style={{
                  width: 26,
                  height: 26,
                  color: gray,
                  backgroundColor: mediumGray,
                  fontSize: 14,
                  borderRadius: '50%',
                  textAlign: 'center',
                  paddingTop: 3,
                }}
              >
                {index}
              </div>
            )
          ) : (
            <div
              style={{
                width: 26,
                height: 26,
                color: 'white',
                backgroundColor: mediumGray,
                fontSize: 14,
                borderRadius: '50%',
                textAlign: 'center',
                paddingTop: 3,
              }}
            >
              <LockOutlined style={{ color: gray, width: 14, height: 14 }} />
            </div>
          )}
          {!isLast && <Box borderLeft={`1px solid ${gray4}`} height="100%" ml="13px" mt={0.5} />}
        </Box>

        {step.enabled ? (
          <>
            <Typography flexGrow={1} mb={isLast ? 0 : 4}>
              <Typography fontSize={16}>{step.title}</Typography>
              <Typography fontSize={14} color="GrayText">
                {step.subTitle}
              </Typography>
            </Typography>
            <Box whiteSpace="nowrap" flexGrow={0} textAlign="center" width={100}>
              <Button
                variant={step.checked ? 'text' : 'contained'}
                onClick={() => {
                  const shouldSkipModal = step.action?.(step.checked);
                  if (!shouldSkipModal) {
                    setModalOpen(true);
                  }
                }}
              >
                {step.checked ? (
                  <Box fontSize={16} textAlign="center">
                    View <RemoveRedEyeOutlined />
                  </Box>
                ) : (
                  'Finalize'
                )}
              </Button>
            </Box>
          </>
        ) : (
          <Box bgcolor={lighterGray} p={1} borderRadius="5px" mb={3}>
            <Typography fontSize={14} color="GrayText">
              {step.onboardingTrackerTitle || step.title}: This step is available just for Admins
            </Typography>
          </Box>
        )}
      </Box>
      {modalOpen && (
        <NonprofitOnboardingFlowContext.Provider
          value={{
            isTransitionAllowed: (action) => action === 'DISMISS',
            send: (action) => {
              if (action === 'DISMISS') {
                setModalOpen(false);
                setId(id + 1);
              }
            },
            state: {},
          }}
        >
          <ModalComponent
            send={(action) => {
              if (action === 'NEXT') {
                setModalOpen(false);
                setId(id + 1);
              }
            }}
            isTransitionAllowed={() => false}
            id={String(id)}
            standaloneMode
          />
        </NonprofitOnboardingFlowContext.Provider>
      )}
    </>
  );
};
